import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    FiUser, FiSearch, FiPlus, FiMinus, FiTrash2, FiShoppingCart,
    FiCreditCard, FiDollarSign, FiCheck, FiX, FiRefreshCw,
    FiScissors, FiPackage, FiStar, FiChevronLeft, FiPhone,
    FiAlertCircle, FiCheckCircle, FiClock, FiArrowRight,
    FiTag, FiUsers, FiFilter, FiEdit2
} from "react-icons/fi";
import {
    api, productsAPI, usersAPI, servicesAPI,
    checkoutAPI, bankAccountsAPI, authAPI
} from "services/api.js";

// ============================================================
// YARDIMCI
// ============================================================
const formatCurrency = (val) =>
    new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(val || 0);

const getInitials = (name = "", surname = "") =>
    ((name[0] || "") + (surname[0] || "")).toUpperCase() || "?";

// ============================================================
// SKELETON LOADER
// ============================================================
const SkeletonCard = () => (
    <div className="animate-pulse p-4 rounded-2xl bg-gray-100 dark:bg-navy-700 space-y-3">
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-navy-600"></div>
            <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-navy-600 rounded-full w-3/4"></div>
                <div className="h-2 bg-gray-200 dark:bg-navy-600 rounded-full w-1/2"></div>
            </div>
        </div>
    </div>
);

const PageSkeleton = () => (
    <div className="p-4 md:p-6 space-y-4 animate-pulse">
        <div className="h-16 bg-gray-100 dark:bg-navy-700 rounded-2xl"></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2].map(i => (
                <div key={i} className="bg-white dark:bg-navy-800 rounded-3xl p-5 border border-gray-100 dark:border-navy-700 space-y-4">
                    <div className="h-6 bg-gray-100 dark:bg-navy-700 rounded-xl w-1/2"></div>
                    {[1, 2, 3].map(j => <SkeletonCard key={j} />)}
                </div>
            ))}
        </div>
    </div>
);

// ============================================================
// STATUS BADGE
// ============================================================
const StatusBadge = ({ status }) => {
    const map = {
        approved: { label: "Onaylandı", cls: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" },
        completed: { label: "Tamamlandı", cls: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400" },
    };
    const cfg = map[status] || { label: status, cls: "bg-gray-100 dark:bg-navy-700 text-gray-500 dark:text-gray-400" };
    return (
        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${cfg.cls}`}>{cfg.label}</span>
    );
};

// ============================================================
// SONUÇ MODALI
// ============================================================
const ResultModal = ({ visible, success, message, onClose }) => {
    if (!visible) return null;
    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-navy-900/60 backdrop-blur-md">
            <div className="bg-white dark:bg-navy-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                 style={{ animation: "scaleIn 0.2s ease-out" }}>
                <div className={`p-8 flex flex-col items-center text-center`}>
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-5 ${success ? "bg-green-50 dark:bg-green-900/20" : "bg-red-50 dark:bg-red-900/20"}`}>
                        {success
                            ? <FiCheckCircle size={42} className="text-green-500" />
                            : <FiAlertCircle size={42} className="text-red-500" />}
                    </div>
                    <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-2">
                        {success ? "İşlem Başarılı!" : "Hata Oluştu"}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm whitespace-pre-line">{message}</p>
                    <button
                        onClick={onClose}
                        className="mt-6 w-full py-3 rounded-2xl font-bold text-white bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/30 transition-all"
                    >
                        Tamam
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// YENİ MÜŞTERİ MODALI
// ============================================================
const NewCustomerModal = ({
                              visible, onClose, form, setForm,
                              existingUserFound, onPhoneChange,
                              onCreate, creating, onSelectExisting
                          }) => {
    if (!visible) return null;
    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-navy-900/60 backdrop-blur-md">
            <div className="bg-white dark:bg-navy-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                 style={{ animation: "scaleIn 0.2s ease-out" }}>
                {/* Header */}
                <div className="p-5 border-b border-gray-100 dark:border-navy-700 flex items-center justify-between bg-gray-50 dark:bg-navy-900">
                    <h2 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2">
                        <FiUser className="text-brand-500" /> Yeni Müşteri Oluştur
                    </h2>
                    <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
                        <FiX size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                    {/* Var olan kullanıcı uyarısı */}
                    {existingUserFound && (
                        <button
                            onClick={() => onSelectExisting(existingUserFound)}
                            className="w-full p-4 bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/30 rounded-2xl flex items-center gap-3 hover:shadow-md transition-all text-left"
                        >
                            <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                {getInitials(existingUserFound.name, existingUserFound.surname)}
                            </div>
                            <div>
                                <div className="text-xs font-bold text-brand-500 mb-0.5">Bu numara zaten kayıtlı! Seçmek için tıkla →</div>
                                <div className="font-bold text-navy-700 dark:text-white">{existingUserFound.name} {existingUserFound.surname}</div>
                            </div>
                            <FiArrowRight className="ml-auto text-brand-500" size={18} />
                        </button>
                    )}

                    {/* Telefon */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                            Telefon Numarası *
                        </label>
                        <div className="flex gap-2">
                            <div className="shrink-0 flex items-center px-3 py-3 bg-gray-100 dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl text-sm font-bold text-gray-600 dark:text-gray-400">
                                +90
                            </div>
                            <input
                                type="tel"
                                maxLength={10}
                                placeholder="5xxxxxxxxx"
                                value={form.phone}
                                onChange={e => onPhoneChange(e.target.value)}
                                autoFocus
                                className="flex-1 bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 py-3 text-base font-bold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all tracking-widest"
                            />
                        </div>
                    </div>

                    {/* İsim Soyisim */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">İsim *</label>
                            <input
                                type="text"
                                placeholder="Ad"
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                className="w-full bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 py-3 text-sm text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Soyisim *</label>
                            <input
                                type="text"
                                placeholder="Soyad"
                                value={form.surname}
                                onChange={e => setForm(p => ({ ...p, surname: e.target.value }))}
                                className="w-full bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl px-4 py-3 text-sm text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-gray-100 dark:border-navy-700 bg-gray-50 dark:bg-navy-900">
                    <button
                        onClick={onCreate}
                        disabled={creating}
                        className="w-full py-3.5 rounded-2xl font-bold text-white bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                        {creating
                            ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Kaydediliyor...</>
                            : <><FiPlus size={16} /> KAYDET VE ADİSYONA GEÇ</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// HİZMET KARTI
// ============================================================
const ServiceCard = ({ srv, staffList, cardSelections, onStaffChange, onAdd }) => {
    const isFav = srv.is_favorite === true || srv.is_favorite === 1;
    const isMale = srv.target_gender === "male";
    const isFemale = srv.target_gender === "female";

    const colorMap = {
        male: { border: "border-blue-200 dark:border-blue-800/40", bg: "bg-blue-50/50 dark:bg-blue-900/10", badge: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400", btn: "bg-blue-500 hover:bg-blue-600 shadow-blue-500/30" },
        female: { border: "border-pink-200 dark:border-pink-800/40", bg: "bg-pink-50/50 dark:bg-pink-900/10", badge: "bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400", btn: "bg-pink-500 hover:bg-pink-600 shadow-pink-500/30" },
        default: { border: "border-gray-100 dark:border-navy-700", bg: "bg-white dark:bg-navy-800", badge: "bg-gray-100 dark:bg-navy-700 text-gray-500 dark:text-gray-400", btn: "bg-brand-500 hover:bg-brand-600 shadow-brand-500/30" },
    };
    const style = isMale ? colorMap.male : isFemale ? colorMap.female : colorMap.default;

    return (
        <div className={`rounded-2xl border p-4 flex flex-col h-full transition-all hover:shadow-md ${style.border} ${style.bg} ${isFav ? "ring-2 ring-amber-400/50 ring-offset-1 ring-offset-white dark:ring-offset-navy-800" : ""}`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        {isFav && <FiStar size={13} className="text-amber-400 fill-amber-400 shrink-0" />}
                        <h6 className="font-bold text-navy-700 dark:text-white text-sm leading-tight truncate">{srv.name}</h6>
                    </div>
                    <div className="font-extrabold text-lg text-navy-700 dark:text-white">{formatCurrency(srv.price)}</div>
                </div>
                {(isMale || isFemale) && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${style.badge}`}>
            {isMale ? "Erkek" : "Kadın"}
          </span>
                )}
            </div>

            {/* Personel Seçici */}
            <div className="mt-auto space-y-2">
                <div className="relative">
                    <FiUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13} />
                    <select
                        value={cardSelections[srv.id] || ""}
                        onChange={e => onStaffChange(srv.id, e.target.value)}
                        className="w-full appearance-none bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl pl-8 pr-8 py-2.5 text-xs font-bold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all cursor-pointer"
                    >
                        <option value="">Personel Seç...</option>
                        {staffList.map(s => <option key={s.id} value={s.id}>{s.name} {s.surname}</option>)}
                    </select>
                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-[10px]">▾</div>
                </div>
                <button
                    onClick={() => onAdd(srv)}
                    className={`w-full py-2.5 rounded-xl text-xs font-bold text-white shadow-md transition-all ${style.btn}`}
                >
                    + Ekle
                </button>
            </div>
        </div>
    );
};

// ============================================================
// ÜRÜN KARTI
// ============================================================
const ProductCard = ({ prod, staffList, cardSelections, onStaffChange, onAdd }) => {
    const isOutOfStock = prod.quantity <= 0;
    const isLowStock = prod.quantity > 0 && prod.quantity <= 5;

    return (
        <div className={`rounded-2xl border p-4 flex flex-col h-full transition-all ${isOutOfStock ? "opacity-50 cursor-not-allowed" : "hover:shadow-md"} bg-white dark:bg-navy-800 border-gray-100 dark:border-navy-700`}>
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                    <h6 className="font-bold text-navy-700 dark:text-white text-sm truncate mb-1">{prod.name}</h6>
                    <div className="font-extrabold text-lg text-navy-700 dark:text-white">{formatCurrency(prod.selling_price)}</div>
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ${isOutOfStock ? "bg-red-100 dark:bg-red-900/20 text-red-500" : isLowStock ? "bg-amber-100 dark:bg-amber-900/20 text-amber-600" : "bg-green-100 dark:bg-green-900/20 text-green-600"}`}>
          {isOutOfStock ? "Stok Yok" : `${prod.quantity} Adet`}
        </span>
            </div>

            {/* Prim personel + Ekle */}
            <div className="mt-auto space-y-2">
                <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Satış Primi (Opsiyonel)</label>
                    <div className="relative">
                        <FiUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13} />
                        <select
                            value={cardSelections[prod.id] || ""}
                            onChange={e => onStaffChange(prod.id, e.target.value)}
                            className="w-full appearance-none bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl pl-8 pr-8 py-2.5 text-xs font-bold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all cursor-pointer"
                        >
                            <option value="">(Yok / Kasa)</option>
                            {staffList.map(s => <option key={s.id} value={s.id}>{s.name} {s.surname}</option>)}
                        </select>
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-[10px]">▾</div>
                    </div>
                </div>
                <button
                    onClick={() => onAdd(prod)}
                    disabled={isOutOfStock}
                    className="w-full py-2.5 rounded-xl text-xs font-bold text-white bg-emerald-500 hover:bg-emerald-600 shadow-md shadow-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Sepete Ekle
                </button>
            </div>
        </div>
    );
};

// ============================================================
// SEPET SATIRI (TEK BİR ÜRÜN/HİZMET)
// ============================================================
const BasketRow = ({ item, onQtyChange, onRemove, onLocalPriceChange, onSavePrice }) => {
    const isProduct = item.type === "product";
    return (
        <div className="group flex items-center gap-3 p-3 rounded-2xl bg-gray-50 dark:bg-navy-900 border border-gray-100 dark:border-navy-700 hover:border-brand-200 dark:hover:border-brand-700 transition-all">
            {/* İkon */}
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isProduct ? "bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600" : "bg-brand-100 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400"}`}>
                {isProduct ? <FiPackage size={15} /> : <FiScissors size={15} />}
            </div>

            {/* İsim + Personel */}
            <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-navy-700 dark:text-white truncate">{item.name}</div>
                {item.staffName && (
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-0.5">
                        <FiUser size={10} /> {item.staffName}
                    </div>
                )}
            </div>

            {/* Miktar */}
            <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => onQtyChange(item.uniqueId, -1)} className="w-7 h-7 rounded-lg bg-gray-200 dark:bg-navy-700 text-gray-600 dark:text-gray-300 flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-500 transition-all text-xs font-bold">
                    <FiMinus size={12} />
                </button>
                <span className="w-6 text-center font-bold text-sm text-navy-700 dark:text-white">{item.quantity}</span>
                <button onClick={() => onQtyChange(item.uniqueId, 1)} className="w-7 h-7 rounded-lg bg-gray-200 dark:bg-navy-700 text-gray-600 dark:text-gray-300 flex items-center justify-center hover:bg-green-100 dark:hover:bg-green-900/20 hover:text-green-500 transition-all text-xs font-bold">
                    <FiPlus size={12} />
                </button>
            </div>

            {/* Fiyat */}
            <div className="shrink-0">
                <input
                    type="number"
                    value={item.price}
                    onChange={e => onLocalPriceChange(item.uniqueId, e.target.value)}
                    onBlur={() => onSavePrice(item.uniqueId, item.price)}
                    className="w-20 bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-600 rounded-xl px-2 py-1.5 text-xs font-bold text-navy-700 dark:text-white outline-none focus:border-brand-500 text-right transition-all"
                />
            </div>

            {/* Sil */}
            <button
                onClick={() => onRemove(item.uniqueId)}
                className="w-7 h-7 rounded-lg shrink-0 text-gray-300 dark:text-gray-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center transition-all"
            >
                <FiTrash2 size={14} />
            </button>
        </div>
    );
};

// ============================================================
// ANA BILEŞEN: Vault_Adisyon
// ============================================================
const Vault_Adisyon = () => {
    const [step, setStep] = useState(1);
    const [loadingData, setLoadingData] = useState(false);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Veriler
    const [activeAppointments, setActiveAppointments] = useState([]);
    const [allCustomers, setAllCustomers] = useState([]);
    const [services, setServices] = useState([]);
    const [products, setProducts] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [bankList, setBankList] = useState([]);

    // Seçimler
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [basket, setBasket] = useState([]);

    // Ödeme
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [selectedBankId, setSelectedBankId] = useState("");

    // Filtreler
    const [customerSearch, setCustomerSearch] = useState("");
    const [itemSearch, setItemSearch] = useState("");
    const [activeTab, setActiveTab] = useState(1); // 1: Hizmet, 2: Ürün
    const [genderFilter, setGenderFilter] = useState("all");

    // Kart personel seçimleri
    const [cardSelections, setCardSelections] = useState({});

    // Modallar
    const [resultModal, setResultModal] = useState({ visible: false, message: "", success: false });
    const [newCustModal, setNewCustModal] = useState(false);
    const [newCustForm, setNewCustForm] = useState({ name: "", surname: "", phone: "" });
    const [existingUserFound, setExistingUserFound] = useState(null);
    const [creatingCust, setCreatingCust] = useState(false);

    // Pagination
    const [custPage, setCustPage] = useState(1);
    const CUST_PER_PAGE = 15;

    // ============================================================
    // VERİ ÇEKME
    // ============================================================
    const fetchAppointments = useCallback(async () => {
        setLoadingAppointments(true);
        try {
            const res = await api.get("/appointments");
            if (res.success) {
                const now = new Date();
                const todayLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
                const filtered = res.appointments
                    .filter(app => app.status === "approved" && app.appointment_date.substring(0, 10) === todayLocal)
                    .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time));
                setActiveAppointments(filtered);
            }
        } catch (err) {
            console.error(err);
            toast.error("Randevular çekilemedi.");
        } finally {
            setLoadingAppointments(false);
        }
    }, []);

    const fetchCustomerTab = useCallback(async (customerId) => {
        try {
            const res = await api.get(`/tabs/${customerId}`);
            if (res.success) {
                const mappedBasket = res.basket.map(item => ({
                    uniqueId: item.id,
                    type: item.type,
                    id: item.item_id,
                    name: item.item_name,
                    price: parseFloat(item.price),
                    quantity: item.quantity,
                    staffId: item.staff_id,
                    staffName: item.staff ? `${item.staff.name} ${item.staff.surname}` : null,
                    appointmentId: item.type === "appointment_existing" ? item.item_id : null,
                }));
                setBasket(mappedBasket);
            }
        } catch (err) {
            console.error("Sepet güncellenemedi:", err);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                const [staffRes, serviceRes, productRes, userRes, bankRes] = await Promise.all([
                    usersAPI.getStaff(),
                    servicesAPI.getAll(),
                    productsAPI.getAll(),
                    usersAPI.getCustomers(),
                    bankAccountsAPI.getAll(),
                ]);
                if (staffRes.success) setStaffList(staffRes.staff.filter(s => s.is_active));
                if (serviceRes.success) setServices(serviceRes.services);
                if (productRes.success) setProducts(productRes.products.filter(p => p.type === "retail"));
                if (userRes.success) setAllCustomers(userRes.customers);
                if (bankRes.success) setBankList(bankRes.bank_accounts);
                await fetchAppointments();
            } catch (err) {
                console.error("Veri hatası", err);
                toast.error("Veriler yüklenirken bağlantı hatası.");
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, [fetchAppointments]);

    // ============================================================
    // HANDLERS
    // ============================================================
    const handleSelectAppointment = async (appt) => {
        setSelectedCustomer(appt.customer);
        try {
            await api.post("/tabs/add", {
                customer_id: appt.customer.id,
                type: "appointment_existing",
                item_id: appt.id,
                item_name: appt.service.name,
                price: parseFloat(appt.price),
                staff_id: appt.staff_id,
                quantity: 1,
            });
            await fetchCustomerTab(appt.customer.id);
        } catch (err) {
            console.error(err);
            toast.error("Randevu adisyona eklenemedi.");
        }
        setStep(2);
        setPaymentMethod("cash");
        setSelectedBankId("");
    };

    const handleNewCustPhoneChange = (val) => {
        const clean = val.replace(/\D/g, "").substring(0, 10);
        setNewCustForm(p => ({ ...p, phone: clean }));
        setExistingUserFound(null);
        if (clean.length >= 7) {
            const found = allCustomers.find(c => c.phone && c.phone.includes(clean));
            if (found) setExistingUserFound(found);
        }
    };

    const handleCreateCustomer = async () => {
        if (existingUserFound) {
            handleSelectCustomerManual(existingUserFound);
            setNewCustModal(false);
            return;
        }
        if (!/^5\d{9}$/.test(newCustForm.phone)) {
            toast.warning("Telefon numarası 5 ile başlamalı ve 10 hane olmalıdır."); return;
        }
        if (!newCustForm.name || !newCustForm.surname) {
            toast.warning("İsim ve Soyisim zorunludur."); return;
        }
        setCreatingCust(true);
        try {
            const res = await authAPI.register({
                name: newCustForm.name, surname: newCustForm.surname,
                phone: "+90" + newCustForm.phone,
                email: "hizlikayit@randevupp.com",
                password: "123456!", password_confirmation: "123456!", role: "customer",
            });
            const newUser = res.user || res.data?.user || res;
            if (newUser?.id) {
                toast.success("Müşteri oluşturuldu.");
                setAllCustomers(prev => [newUser, ...prev]);
                handleSelectCustomerManual(newUser);
                setNewCustModal(false);
                setNewCustForm({ name: "", surname: "", phone: "" });
            }
        } catch (err) {
            toast.error("Hata: " + (err.response?.data?.message || err.message));
        } finally {
            setCreatingCust(false);
        }
    };

    const handleSelectCustomerManual = (customer) => {
        setSelectedCustomer(customer);
        fetchCustomerTab(customer.id);
        setStep(2);
        setPaymentMethod("cash");
        setSelectedBankId("");
        setCustPage(1);
    };

    const handleAddService = async (service) => {
        const staffId = cardSelections[service.id];
        if (!staffId) { toast.warning("Lütfen bu hizmeti kimin vereceğini seçin."); return; }
        try {
            await api.post("/tabs/add", {
                customer_id: selectedCustomer.id, type: "service_new",
                item_id: service.id, item_name: service.name,
                price: parseFloat(service.price), staff_id: staffId, quantity: 1,
            });
            toast.success("Hizmet eklendi");
            fetchCustomerTab(selectedCustomer.id);
        } catch (err) { toast.error("Hizmet eklenemedi"); }
    };

    const handleAddProduct = async (product) => {
        if (product.quantity <= 0) { toast.error("Stokta ürün kalmadı!"); return; }
        const staffId = cardSelections[product.id];
        try {
            await api.post("/tabs/add", {
                customer_id: selectedCustomer.id, type: "product",
                item_id: product.id, item_name: product.name,
                price: parseFloat(product.selling_price), staff_id: staffId || null, quantity: 1,
            });
            toast.success("Ürün sepete eklendi");
            fetchCustomerTab(selectedCustomer.id);
        } catch (err) { toast.error("Ürün eklenemedi"); }
    };

    const updateQuantity = async (uniqueId, change) => {
        const item = basket.find(i => i.uniqueId === uniqueId);
        if (!item) return;
        const newQty = Math.max(1, item.quantity + change);
        try {
            await api.put(`/tabs/item/${uniqueId}`, { quantity: newQty });
            fetchCustomerTab(selectedCustomer.id);
        } catch (err) { console.error(err); }
    };

    const handleRemoveItem = async (uniqueId) => {
        try {
            await api.delete(`/tabs/item/${uniqueId}`);
            fetchCustomerTab(selectedCustomer.id);
        } catch (err) { console.error(err); }
    };

    const handleLocalPriceChange = (uniqueId, newPrice) => {
        setBasket(prev => prev.map(item =>
            item.uniqueId === uniqueId ? { ...item, price: newPrice } : item
        ));
    };

    const handleSavePrice = async (uniqueId, currentPrice) => {
        const finalPrice = parseFloat(currentPrice) || 0;
        try {
            await api.put(`/tabs/item/${uniqueId}`, { price: finalPrice });
            fetchCustomerTab(selectedCustomer.id);
        } catch (err) { console.error(err); }
    };

    const handleCardStaffChange = (itemId, staffId) => {
        setCardSelections(prev => ({ ...prev, [itemId]: staffId }));
    };

    const totalAmount = useMemo(
        () => basket.reduce((acc, item) => acc + item.price * item.quantity, 0),
        [basket]
    );

    const handleCompleteSale = async () => {
        if (basket.length === 0) return;
        if (paymentMethod === "card" && !selectedBankId) {
            toast.warning("Lütfen bir banka hesabı seçin."); return;
        }
        setIsProcessing(true);
        try {
            const payload = {
                customer_id: selectedCustomer.id,
                customer_name: `${selectedCustomer.name} ${selectedCustomer.surname}`,
                payment_method: paymentMethod,
                bank_account_id: paymentMethod === "card" ? selectedBankId : null,
                items: basket.map(item => ({
                    type: item.type, id: item.id, name: item.name,
                    price: item.price, quantity: item.quantity,
                    staff_id: item.staffId || null, appointment_id: item.appointmentId || null,
                })),
            };
            const res = await checkoutAPI.process(payload);
            if (res.success) {
                let paymentInfo = paymentMethod === "cash" ? "Nakit" : "Kredi Kartı";
                if (paymentMethod === "card" && selectedBankId) {
                    const bankName = bankList.find(b => b.id == selectedBankId)?.name;
                    paymentInfo += ` (${bankName})`;
                }
                setResultModal({
                    visible: true, success: true,
                    message: `Satış Tamamlandı!\nMüşteri: ${selectedCustomer.name} ${selectedCustomer.surname}\nÖdeme: ${paymentInfo}\nToplam: ${formatCurrency(totalAmount)}`,
                });
                setBasket([]); setStep(1); setCardSelections({});
                setPaymentMethod("cash"); setSelectedBankId("");
                fetchAppointments();
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Hata oluştu";
            setResultModal({ visible: true, success: false, message: "Hata: " + errorMsg });
        } finally {
            setIsProcessing(false);
        }
    };

    // ============================================================
    // FİLTRELENMİŞ VERİLER
    // ============================================================
    const filteredServices = useMemo(() => services
        .filter(s => {
            const matchSearch = s.name.toLowerCase().includes(itemSearch.toLowerCase());
            let matchGender = true;
            if (genderFilter === "male") matchGender = s.target_gender === "male" || s.target_gender === "unisex" || !s.target_gender;
            else if (genderFilter === "female") matchGender = s.target_gender === "female" || s.target_gender === "unisex" || !s.target_gender;
            return matchSearch && matchGender;
        })
        .sort((a, b) => {
            const aFav = (a.is_favorite === true || a.is_favorite === 1) ? 1 : 0;
            const bFav = (b.is_favorite === true || b.is_favorite === 1) ? 1 : 0;
            return bFav - aFav;
        }), [services, itemSearch, genderFilter]);

    const filteredProducts = useMemo(() =>
            products.filter(p => p.name.toLowerCase().includes(itemSearch.toLowerCase())),
        [products, itemSearch]);

    const filteredCustomers = useMemo(() => allCustomers.filter(c =>
        c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.surname.toLowerCase().includes(customerSearch.toLowerCase()) ||
        (c.phone && c.phone.includes(customerSearch))
    ), [allCustomers, customerSearch]);

    const pagedCustomers = useMemo(() =>
            filteredCustomers.slice(0, custPage * CUST_PER_PAGE),
        [filteredCustomers, custPage]);

    // ============================================================
    // LOADING
    // ============================================================
    if (loadingData) return <PageSkeleton />;

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className="p-4 md:p-6 min-h-screen">
            <ToastContainer position="bottom-right" theme="colored" toastClassName="!rounded-2xl !font-bold" />

            {/* Global animasyonlar */}
            <style>{`
        @keyframes scaleIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .adi-fadein { animation: fadeInUp 0.3s ease-out; }
      `}</style>

            {/* ==========================================
          ADIM 1: MÜŞTERI SEÇİMİ
      ========================================== */}
            {step === 1 && (
                <div className="adi-fadein space-y-6">
                    {/* Sayfa Başlığı */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-navy-700 dark:text-white">Adisyon</h1>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Müşteriyi seçin veya oluşturun, ardından ödeme alın.</p>
                        </div>
                        {/* Hızlı Müşteri Oluştur */}
                        <button
                            onClick={() => { setNewCustModal(true); setNewCustForm({ name: "", surname: "", phone: "" }); setExistingUserFound(null); }}
                            className="flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold text-white bg-gradient-to-r from-brand-500 to-purple-500 hover:from-brand-600 hover:to-purple-600 shadow-lg shadow-brand-500/30 transition-all w-full sm:w-auto"
                        >
                            <FiPlus size={18} /> Hızlı Müşteri Oluştur
                        </button>
                    </div>

                    {/* İki kolon: Randevular + Manuel Arama */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* SOL: Bugünün Randevuları */}
                        <div className="bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden flex flex-col">
                            {/* Kart Başlık */}
                            <div className="px-5 py-4 border-b border-gray-100 dark:border-navy-700 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-brand-100 dark:bg-brand-900/20 flex items-center justify-center">
                                        <FiClock className="text-brand-500" size={17} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-navy-700 dark:text-white text-base">Bugünün Randevuları</h3>
                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                            {activeAppointments.length} onaylanmış randevu
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={fetchAppointments}
                                    disabled={loadingAppointments}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700 transition-all"
                                >
                                    <FiRefreshCw size={13} className={loadingAppointments ? "animate-spin" : ""} /> Yenile
                                </button>
                            </div>

                            {/* Liste */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 520, scrollbarWidth: "thin" }}>
                                {loadingAppointments ? (
                                    [...Array(4)].map((_, i) => <SkeletonCard key={i} />)
                                ) : activeAppointments.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-navy-700 flex items-center justify-center mb-3">
                                            <FiClock size={24} className="text-gray-300 dark:text-gray-600" />
                                        </div>
                                        <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">Bugüne ait onaylanmış randevu yok.</p>
                                    </div>
                                ) : (
                                    activeAppointments.map(appt => (
                                        <button
                                            key={appt.id}
                                            onClick={() => handleSelectAppointment(appt)}
                                            className="w-full text-left group relative p-4 rounded-2xl border border-gray-100 dark:border-navy-700 bg-gray-50 dark:bg-navy-900 hover:border-brand-300 dark:hover:border-brand-700 hover:shadow-md transition-all"
                                        >
                                            {/* Saat badge */}
                                            <span className="absolute top-3 right-3 text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                        {appt.appointment_time.substring(0, 5)}
                      </span>
                                            <div className="flex items-center gap-3 pr-14">
                                                {/* Avatar */}
                                                <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                                    {getInitials(appt.customer.name, appt.customer.surname)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-bold text-navy-700 dark:text-white text-base truncate">
                                                        {appt.customer.name} {appt.customer.surname}
                                                    </div>
                                                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <FiScissors size={11} /> {appt.service.name}
                            </span>
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <FiUser size={11} /> {appt.staff.name}
                            </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="mt-3 flex items-center justify-between">
                                                <StatusBadge status={appt.status} />
                                                <span className="text-xs font-bold text-brand-500 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          Adisyon Aç <FiArrowRight size={12} />
                        </span>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* SAĞ: Manuel Müşteri Arama */}
                        <div className="bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden flex flex-col">
                            {/* Kart Başlık */}
                            <div className="px-5 py-4 border-b border-gray-100 dark:border-navy-700 flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                                    <FiSearch className="text-emerald-500" size={17} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-navy-700 dark:text-white text-base">Kayıtlı Müşteri Ara</h3>
                                    <p className="text-xs text-gray-400 dark:text-gray-500">Online randevusuz işlem</p>
                                </div>
                            </div>

                            {/* Arama Kutusu */}
                            <div className="px-4 pt-4">
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                                    <input
                                        type="text"
                                        placeholder="İsim, soyisim veya telefon..."
                                        value={customerSearch}
                                        onChange={e => { setCustomerSearch(e.target.value); setCustPage(1); }}
                                        className="w-full bg-gray-50 dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl pl-10 pr-4 py-3 text-sm text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all"
                                    />
                                </div>
                            </div>

                            {/* Müşteri Listesi */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{ maxHeight: 420, scrollbarWidth: "thin" }}>
                                {pagedCustomers.length === 0 ? (
                                    <div className="text-center py-10 text-gray-400 dark:text-gray-600 text-sm">
                                        {customerSearch ? "Müşteri bulunamadı." : "Aramak için yazmaya başlayın."}
                                    </div>
                                ) : (
                                    pagedCustomers.map(cust => (
                                        <button
                                            key={cust.id}
                                            onClick={() => handleSelectCustomerManual(cust)}
                                            className="w-full text-left flex items-center gap-3 p-3 rounded-2xl border border-gray-100 dark:border-navy-700 hover:border-brand-300 dark:hover:border-brand-700 hover:bg-brand-50/50 dark:hover:bg-brand-900/10 transition-all group"
                                        >
                                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                                                {getInitials(cust.name, cust.surname)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm text-navy-700 dark:text-white truncate">{cust.name} {cust.surname}</div>
                                                {cust.phone && <div className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1"><FiPhone size={10} />{cust.phone}</div>}
                                            </div>
                                            <FiArrowRight size={15} className="text-gray-300 dark:text-gray-600 group-hover:text-brand-500 transition-colors shrink-0" />
                                        </button>
                                    ))
                                )}

                                {/* Daha fazla göster */}
                                {filteredCustomers.length > pagedCustomers.length && (
                                    <button
                                        onClick={() => setCustPage(p => p + 1)}
                                        className="w-full py-2.5 text-xs font-bold text-brand-500 hover:text-brand-600 border border-dashed border-brand-200 dark:border-brand-800/40 rounded-xl hover:bg-brand-50 dark:hover:bg-brand-900/10 transition-all"
                                    >
                                        {filteredCustomers.length - pagedCustomers.length} kişi daha göster
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ==========================================
          ADIM 2: ADİSYON DETAY
      ========================================== */}
            {step === 2 && selectedCustomer && (
                <div className="adi-fadein space-y-5">

                    {/* Müşteri Bilgi Bandı */}
                    <div className="bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-lg shadow-brand-500/30">
                                {getInitials(selectedCustomer.name, selectedCustomer.surname)}
                            </div>
                            <div>
                                <div className="text-[10px] font-bold text-brand-500 uppercase tracking-widest mb-0.5">Aktif Adisyon</div>
                                <h2 className="text-xl font-bold text-navy-700 dark:text-white">{selectedCustomer.name} {selectedCustomer.surname}</h2>
                                {selectedCustomer.phone && (
                                    <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-0.5"><FiPhone size={11} />{selectedCustomer.phone}</div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => { setStep(1); setBasket([]); setSelectedCustomer(null); }}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700 transition-all w-full sm:w-auto justify-center"
                        >
                            <FiChevronLeft size={16} /> Müşteri Değiştir
                        </button>
                    </div>

                    {/* Ana İçerik: Katalog + Sepet */}
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">

                        {/* SOL: Katalog (Hizmet / Ürün) */}
                        <div className="xl:col-span-7 bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden flex flex-col">

                            {/* Tab Header */}
                            <div className="flex border-b border-gray-100 dark:border-navy-700">
                                {[
                                    { id: 1, label: "Hizmetler", icon: <FiScissors size={15}/>, count: filteredServices.length },
                                    { id: 2, label: "Ürünler", icon: <FiPackage size={15}/>, count: filteredProducts.length },
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => { setActiveTab(tab.id); setItemSearch(""); setGenderFilter("all"); }}
                                        className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold transition-all border-b-2 
                      ${activeTab === tab.id
                                            ? "border-brand-500 text-brand-500"
                                            : "border-transparent text-gray-500 dark:text-gray-400 hover:text-navy-700 dark:hover:text-white"}`}
                                    >
                                        {tab.icon} {tab.label}
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${activeTab === tab.id ? "bg-brand-100 dark:bg-brand-900/30 text-brand-500" : "bg-gray-100 dark:bg-navy-700 text-gray-500 dark:text-gray-400"}`}>
                      {tab.count}
                    </span>
                                    </button>
                                ))}
                            </div>

                            {/* Filtre araçları */}
                            <div className="px-4 pt-4 pb-3 space-y-3">
                                {/* Arama */}
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                                    <input
                                        type="text"
                                        placeholder={activeTab === 1 ? "Hizmet ara..." : "Ürün ara..."}
                                        value={itemSearch}
                                        onChange={e => setItemSearch(e.target.value)}
                                        className="w-full bg-gray-50 dark:bg-navy-900 border border-gray-200 dark:border-navy-600 rounded-xl pl-10 pr-4 py-2.5 text-sm text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all"
                                    />
                                </div>

                                {/* Cinsiyet filtresi (sadece Hizmetler tabında) */}
                                {activeTab === 1 && (
                                    <div className="flex gap-1.5 bg-gray-100 dark:bg-navy-900 p-1 rounded-xl w-fit">
                                        {[
                                            { val: "all", label: "Tümü" },
                                            { val: "female", label: "Kadın" },
                                            { val: "male", label: "Erkek" },
                                        ].map(f => (
                                            <button
                                                key={f.val}
                                                onClick={() => setGenderFilter(f.val)}
                                                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all
                          ${genderFilter === f.val
                                                    ? "bg-white dark:bg-navy-800 text-navy-700 dark:text-white shadow-sm"
                                                    : "text-gray-500 dark:text-gray-400 hover:text-navy-700 dark:hover:text-white"}`}
                                            >
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Grid */}
                            <div className="flex-1 overflow-y-auto px-4 pb-4" style={{ maxHeight: 600, scrollbarWidth: "thin" }}>
                                {activeTab === 1 && (
                                    filteredServices.length === 0 ? (
                                        <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-600 text-sm">Hizmet bulunamadı.</div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-3">
                                            {filteredServices.map(srv => (
                                                <ServiceCard
                                                    key={srv.id}
                                                    srv={srv}
                                                    staffList={staffList}
                                                    cardSelections={cardSelections}
                                                    onStaffChange={handleCardStaffChange}
                                                    onAdd={handleAddService}
                                                />
                                            ))}
                                        </div>
                                    )
                                )}
                                {activeTab === 2 && (
                                    filteredProducts.length === 0 ? (
                                        <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-600 text-sm">Ürün bulunamadı.</div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-3">
                                            {filteredProducts.map(prod => (
                                                <ProductCard
                                                    key={prod.id}
                                                    prod={prod}
                                                    staffList={staffList}
                                                    cardSelections={cardSelections}
                                                    onStaffChange={handleCardStaffChange}
                                                    onAdd={handleAddProduct}
                                                />
                                            ))}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>

                        {/* SAĞ: Sepet + Ödeme */}
                        <div className="xl:col-span-5 flex flex-col gap-4">

                            {/* Sepet Kartı */}
                            <div className="bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden flex flex-col">

                                {/* Sepet Başlık */}
                                <div className="px-5 py-4 border-b border-gray-100 dark:border-navy-700 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
                                            <FiShoppingCart className="text-amber-500" size={17} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-navy-700 dark:text-white text-base">Sepet</h3>
                                            <p className="text-xs text-gray-400 dark:text-gray-500">{basket.length} kalem</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wide">Toplam</div>
                                        <div className="text-xl font-extrabold text-navy-700 dark:text-white">{formatCurrency(totalAmount)}</div>
                                    </div>
                                </div>

                                {/* Sepet İçeriği */}
                                <div className="overflow-y-auto p-4 space-y-2" style={{ maxHeight: 380, scrollbarWidth: "thin" }}>
                                    {basket.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-navy-700 flex items-center justify-center mb-3">
                                                <FiShoppingCart size={24} className="text-gray-300 dark:text-gray-600" />
                                            </div>
                                            <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">Sepetiniz boş.</p>
                                            <p className="text-gray-400 dark:text-gray-600 text-xs mt-1">Sol taraftan hizmet veya ürün ekleyin.</p>
                                        </div>
                                    ) : (
                                        basket.map(item => (
                                            <BasketRow
                                                key={item.uniqueId}
                                                item={item}
                                                onQtyChange={updateQuantity}
                                                onRemove={handleRemoveItem}
                                                onLocalPriceChange={handleLocalPriceChange}
                                                onSavePrice={handleSavePrice}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Ödeme Kartı */}
                            <div className="bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm p-5 space-y-4">
                                {/* Başlık */}
                                <h3 className="font-bold text-navy-700 dark:text-white text-base flex items-center gap-2">
                                    <FiCreditCard className="text-brand-500" /> Ödeme Yöntemi
                                </h3>

                                {/* Ödeme Seçenekleri */}
                                <div className="grid grid-cols-2 gap-3">
                                    {/* Nakit */}
                                    <button
                                        onClick={() => { setPaymentMethod("cash"); setSelectedBankId(""); }}
                                        className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all
                      ${paymentMethod === "cash"
                                            ? "border-green-400 bg-green-50 dark:bg-green-900/10 shadow-md shadow-green-500/15"
                                            : "border-gray-200 dark:border-navy-600 bg-gray-50 dark:bg-navy-900 hover:border-green-300"}`}
                                    >
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${paymentMethod === "cash" ? "bg-green-100 dark:bg-green-900/30" : "bg-gray-200 dark:bg-navy-700"}`}>
                                            <FiDollarSign size={20} className={paymentMethod === "cash" ? "text-green-600" : "text-gray-400"} />
                                        </div>
                                        <div className="text-center">
                                            <div className={`text-sm font-bold ${paymentMethod === "cash" ? "text-green-700 dark:text-green-400" : "text-gray-500 dark:text-gray-400"}`}>Nakit</div>
                                        </div>
                                        {paymentMethod === "cash" && <FiCheck size={14} className="text-green-600 absolute top-2 right-2" />}
                                    </button>

                                    {/* Kart / Banka */}
                                    <button
                                        onClick={() => setPaymentMethod("card")}
                                        className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all relative
                      ${paymentMethod === "card"
                                            ? "border-brand-400 bg-brand-50 dark:bg-brand-900/10 shadow-md shadow-brand-500/15"
                                            : "border-gray-200 dark:border-navy-600 bg-gray-50 dark:bg-navy-900 hover:border-brand-300"}`}
                                    >
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${paymentMethod === "card" ? "bg-brand-100 dark:bg-brand-900/30" : "bg-gray-200 dark:bg-navy-700"}`}>
                                            <FiCreditCard size={20} className={paymentMethod === "card" ? "text-brand-600 dark:text-brand-400" : "text-gray-400"} />
                                        </div>
                                        <div className="text-center">
                                            <div className={`text-sm font-bold ${paymentMethod === "card" ? "text-brand-700 dark:text-brand-400" : "text-gray-500 dark:text-gray-400"}`}>Kart / Banka</div>
                                        </div>
                                        {paymentMethod === "card" && <FiCheck size={14} className="text-brand-600 absolute top-2 right-2" />}
                                    </button>
                                </div>

                                {/* Banka Seçimi */}
                                {paymentMethod === "card" && (
                                    <div style={{ animation: "fadeInUp 0.2s ease-out" }}>
                                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Banka Hesabı *</label>
                                        <div className="relative">
                                            <select
                                                value={selectedBankId}
                                                onChange={e => setSelectedBankId(e.target.value)}
                                                className="w-full appearance-none bg-white dark:bg-navy-900 border border-brand-200 dark:border-brand-800/50 rounded-xl px-4 pr-10 py-3 text-sm font-bold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all cursor-pointer"
                                            >
                                                <option value="">Hesap Seç...</option>
                                                {bankList.map(bank => (
                                                    <option key={bank.id} value={bank.id}>
                                                        {bank.name} (Komisyon: %{bank.fee_percentage})
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-[10px]">▾</div>
                                        </div>
                                    </div>
                                )}

                                {/* Toplam Özet */}
                                <div className="flex items-center justify-between py-3 border-t border-gray-100 dark:border-navy-700">
                                    <span className="text-sm font-bold text-gray-500 dark:text-gray-400">Genel Toplam</span>
                                    <span className="text-2xl font-extrabold text-navy-700 dark:text-white">{formatCurrency(totalAmount)}</span>
                                </div>

                                {/* Satışı Tamamla Butonu */}
                                <button
                                    onClick={handleCompleteSale}
                                    disabled={isProcessing || basket.length === 0}
                                    className="w-full py-4 rounded-2xl font-bold text-white text-base bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-xl shadow-green-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isProcessing ? (
                                        <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> İşleniyor...</>
                                    ) : (
                                        <><FiCheckCircle size={20} /> SATIŞI TAMAMLA</>
                                    )}
                                </button>

                                {basket.length === 0 && (
                                    <p className="text-xs text-center text-gray-400 dark:text-gray-600">Satışı tamamlamak için sepete ürün ekleyin.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ==========================================
          MODALLAR
      ========================================== */}
            <ResultModal
                visible={resultModal.visible}
                success={resultModal.success}
                message={resultModal.message}
                onClose={() => setResultModal(p => ({ ...p, visible: false }))}
            />

            <NewCustomerModal
                visible={newCustModal}
                onClose={() => setNewCustModal(false)}
                form={newCustForm}
                setForm={setNewCustForm}
                existingUserFound={existingUserFound}
                onPhoneChange={handleNewCustPhoneChange}
                onCreate={handleCreateCustomer}
                creating={creatingCust}
                onSelectExisting={(user) => { handleSelectCustomerManual(user); setNewCustModal(false); }}
            />
        </div>
    );
};

export default Vault_Adisyon;